import { useCallback, useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// CRUD for english_courses + english_lectures. The course page is
// small enough that one fetch-everything hook is cheaper than the
// network round-trips a per-course hook would cost.
//
// `courses` carries `completed_lectures` and `total_lectures` so the
// dashboard's progress strip doesn't need to count lectures itself
// — the migration trigger keeps those columns in sync after every
// lecture upsert.
export function useEnglishCourses() {
  const { user } = useAuth()
  const [courses, setCourses] = useState([])
  const [lectures, setLectures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setError(null)
    const [cRes, lRes] = await Promise.all([
      supabase
        .from('english_courses')
        .select('*')
        .order('status', { ascending: true })
        .order('last_studied_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('english_lectures')
        .select('*')
        .order('section', { ascending: true, nullsFirst: true })
        .order('lecture_number', { ascending: true, nullsFirst: true }),
    ])
    if (cRes.error) { setError(cRes.error.message); setLoading(false); return }
    if (lRes.error) { setError(lRes.error.message); setLoading(false); return }
    setCourses(cRes.data || [])
    setLectures(lRes.data || [])
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const addCourse = useCallback(async (payload) => {
    if (!user) return { error: 'not signed in' }
    const row = {
      user_id: user.id,
      title: (payload?.title || '').trim(),
      platform: (payload?.platform || 'udemy').trim() || 'udemy',
      url: (payload?.url || '').trim() || null,
      instructor: (payload?.instructor || '').trim() || null,
      total_lectures: Number.isFinite(+payload?.total_lectures) ? +payload.total_lectures : 0,
      status: payload?.status || 'active',
    }
    if (!row.title) return { error: 'title required' }
    const { data, error: err } = await supabase
      .from('english_courses')
      .insert(row)
      .select('*')
      .single()
    if (err) return { error: err.message }
    setCourses((prev) => [data, ...prev])
    return { course: data }
  }, [user])

  const updateCourse = useCallback(async (id, patch) => {
    const { data, error: err } = await supabase
      .from('english_courses')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single()
    if (err) return { error: err.message }
    setCourses((prev) => prev.map((c) => (c.id === id ? data : c)))
    return { course: data }
  }, [])

  const removeCourse = useCallback(async (id) => {
    const snapshot = courses
    setCourses((prev) => prev.filter((c) => c.id !== id))
    setLectures((prev) => prev.filter((l) => l.course_id !== id))
    const { error: err } = await supabase.from('english_courses').delete().eq('id', id)
    if (err) {
      setCourses(snapshot)
      // lectures will re-pull on next fetch; not worth retaining a
      // second snapshot just for the rare rollback case.
      return { error: err.message }
    }
    return { ok: true }
  }, [courses])

  const addLecture = useCallback(async ({ course_id, title, section, lecture_number, duration_minutes, notes }) => {
    if (!user) return { error: 'not signed in' }
    const trimmed = (title || '').trim()
    if (!course_id) return { error: 'course_id required' }
    if (!trimmed) return { error: 'title required' }
    const row = {
      user_id: user.id,
      course_id,
      title: trimmed,
      section: (section || '').trim() || null,
      lecture_number: Number.isFinite(+lecture_number) ? +lecture_number : null,
      duration_minutes: Number.isFinite(+duration_minutes) ? +duration_minutes : null,
      notes: (notes || '').trim() || null,
    }
    const { data, error: err } = await supabase
      .from('english_lectures')
      .insert(row)
      .select('*')
      .single()
    if (err) return { error: err.message }
    setLectures((prev) => [...prev, data])
    // The trigger updates english_courses cached counts server-side;
    // re-fetch the affected course so the progress bar reflects it.
    refreshCourseRow(course_id, setCourses)
    return { lecture: data }
  }, [user])

  const toggleLectureComplete = useCallback(async (id) => {
    const target = lectures.find((l) => l.id === id)
    if (!target) return { error: 'not found' }
    const nextCompletedAt = target.completed_at ? null : new Date().toISOString()
    // Optimistic flip
    setLectures((prev) => prev.map((l) => (l.id === id ? { ...l, completed_at: nextCompletedAt } : l)))
    const { error: err } = await supabase
      .from('english_lectures')
      .update({ completed_at: nextCompletedAt, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      // Rollback
      setLectures((prev) => prev.map((l) => (l.id === id ? target : l)))
      return { error: err.message }
    }
    refreshCourseRow(target.course_id, setCourses)
    return { ok: true }
  }, [lectures])

  const updateLecture = useCallback(async (id, patch) => {
    const { data, error: err } = await supabase
      .from('english_lectures')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single()
    if (err) return { error: err.message }
    setLectures((prev) => prev.map((l) => (l.id === id ? data : l)))
    return { lecture: data }
  }, [])

  const removeLecture = useCallback(async (id) => {
    const target = lectures.find((l) => l.id === id)
    if (!target) return { error: 'not found' }
    setLectures((prev) => prev.filter((l) => l.id !== id))
    const { error: err } = await supabase.from('english_lectures').delete().eq('id', id)
    if (err) {
      setLectures((prev) => [...prev, target])
      return { error: err.message }
    }
    refreshCourseRow(target.course_id, setCourses)
    return { ok: true }
  }, [lectures])

  return {
    courses,
    lectures,
    loading,
    error,
    refetch: fetch,
    addCourse,
    updateCourse,
    removeCourse,
    addLecture,
    updateLecture,
    toggleLectureComplete,
    removeLecture,
  }
}

// After any lecture mutation the migration trigger updates the
// parent course's cached completed_lectures + last_studied_at. We
// re-pull just that row so the UI reflects it without a full refetch.
async function refreshCourseRow(courseId, setCourses) {
  const { data, error } = await supabase
    .from('english_courses')
    .select('*')
    .eq('id', courseId)
    .maybeSingle()
  if (error || !data) return
  setCourses((prev) => prev.map((c) => (c.id === courseId ? data : c)))
}

// Dashboard tile probe — returns the most-recently-studied active
// course's progress without pulling every lecture row. Cheap enough
// to fire on every dashboard open.
export function useEnglishCourseProgress() {
  const { user } = useAuth()
  const [course, setCourse] = useState(null)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return () => { alive = false }
    }
    Promise.all([
      supabase
        .from('english_courses')
        .select('id, title, total_lectures, completed_lectures, last_studied_at, status')
        .eq('status', 'active')
        .order('last_studied_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('english_courses')
        .select('id', { count: 'exact', head: true }),
    ]).then(([cRes, countRes]) => {
      if (!alive) return
      if (!cRes.error) setCourse(cRes.data || null)
      if (!countRes.error) setCount(countRes.count || 0)
      setLoading(false)
    })
    return () => { alive = false }
  }, [user])

  return { course, count, loading }
}
